import React, { useState, useContext, useMemo, useCallback } from 'react'
import { useUserContext } from '@/UserContext'
import useInput, { Conditions } from '@/services/useInput'
import * as Constants from '@/services/Constants'
import { classifyAudioInputs, queryCurrentMediaSelectionWithFallback } from '@/Logic'
import { Trans, useTranslation } from 'react-i18next'
import i18n from 'i18next'
import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import ElectronIPC from '@/services/ElectronIPC'
import useRoomActions from '@/services/useRoomActions'
import { VolumeMeter } from '@/components/VolumeMeter'
import Loader from 'react-loader-spinner'
import { MuseLogo } from '@/components/MuseLogo'
import { useInIframe } from '@/services/useInIframe'
import { TextInput } from '@/UIComponents2/TextInput'
import UserIcon from '@/CustomIcons/UserIcon'
import { CameraPreview } from '@/components/CameraPreview'
import { AppContext, AppContextType } from '@/AppContext'
import MenuButton from '@/UIComponents2/MenuButton/MenuButton'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import MicrophoneOffIcon from '@/CustomIcons/MicrophoneOffIcon'
import { Checkbox } from '@/UIComponents2/Checkbox'
import SpeakerIcon from '@/CustomIcons/SpeakerIcon'
import { Button } from '@/UIComponents2/Button'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import { cn } from '@/utlis/cn'
import PlayIcon from '@/CustomIcons/PlayIcon'
import CameraIcon from '@/CustomIcons/CameraIcon'
import StopIcon from '@/CustomIcons/StopIcon'
import Tooltip from '@/UIComponents2/Tooltip'
import { OverlayControlButton } from '@/components/OverlayControlButton'
import VirtualBackgroundIcon from '@/CustomIcons/VirtualBackgroundIcon'
import StatusErrorIcon from '@/CustomIcons/StatusErrorIcon'
import CameraOffIcon from '@/CustomIcons/CameraOffIcon'
import { SpeakerTest } from '@/components/SpeakerTest'

function GuestJoinForm({
    onSubmit,
    loading,
    joinCode,
    joinLinkMetadata,
    isPermissionsDenied,
    clientType,
    setIsBlockedModalOpen,
}: {
    onSubmit: (formdata: any) => void
    loading: boolean
    joinCode: string
    joinLinkMetadata: { [key: string]: string }
    isPermissionsDenied: boolean
    clientType: string | null
    setIsBlockedModalOpen: (open: boolean) => void
}) {
    const isViewer = clientType === 'listenonly'
    const [userState] = useUserContext()
    const { dispatch: appDispatch, state: appState } = useContext(AppContext) as AppContextType
    const { calcIsMyAudioDeviceAllMuted, toggleMyAudioDeviceMute, toggleMyVideoDeviceMute, isMyVideoDeviceMuted } =
        useRoomActions()
    const videoMuted = isMyVideoDeviceMuted()

    const currMediaSelection = userState.mediaSelection

    const currentVideo = queryCurrentMediaSelectionWithFallback(
        userState.mediaSelection,
        userState.mediaSources,
        'video'
    )

    const selectedSpeaker = queryCurrentMediaSelectionWithFallback(
        currMediaSelection,
        userState.mediaSources,
        'audioOutput'
    )
    const speakerOptions = useMemo(() => {
        return userState.mediaSources.audioOutputs.map((source) => ({
            value: source.deviceId,
            label: source.label,
        }))
    }, [userState.mediaSources.audioOutputs])

    const { setMediaSelection, toggleAudioDeviceSelection } = useMediaSelectionActions()
    const audioInputs = useMemo(
        () => classifyAudioInputs(userState.mediaSources.audioInputs),
        [userState.mediaSources.audioInputs]
    )

    const { t } = useTranslation()

    const { formdata, bind, submit } = useInput(inputModel)

    const [playingTest, setPlayingTest] = useState(false)

    const onSubmitHandler = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault()

            const { hasErrors } = await submit()
            if (!hasErrors) {
                formdata.clientKind = Constants.clientKind
                onSubmit(formdata)
            }
        },
        [submit, formdata, onSubmit]
    )

    const atrack = useMemo(() => {
        const currentDevice = currMediaSelection.audioInputs[0]?.deviceId ?? '_none'
        const currentSource = audioInputs?.external?.find((input) => input?.deviceId === currentDevice)
        const currentSelection = currentSource
            ? userState.mediaSelection.audioInputs.find((i) => i.deviceId === currentSource.deviceId)
            : null
        const atrack = currentSelection
            ? userState.mediaStreams.audioInputs.find((i) => i.info.deviceId === currentSelection.deviceId)
            : null
        return atrack
    }, [audioInputs, userState.mediaStreams.audioInputs])

    const toggleVirtualBackgroundModal = () => {
        appDispatch({
            type: 'update_app_state',
            value: { showChooseVirtualBackgroundModal: !appState.appState.showChooseVirtualBackgroundModal },
        })
    }

    const handleSpeakerChange = useCallback(
        (deviceId: string) => {
            setMediaSelection({ audioOutput: deviceId })
        },
        [setMediaSelection]
    )

    const inIframe = useInIframe()
    const audioMuted = calcIsMyAudioDeviceAllMuted()

    const museLogo = () => {
        if (!inIframe) {
            return null
        }
        return (
            <div className="flex justify-center">
                <div className="max-w-[100px] mt-6">
                    <a href="https://musesessions.co/" target="_blank">
                        <MuseLogo />
                    </a>
                </div>
            </div>
        )
    }

    return (
        <div className={cn('flex flex-col', 'gap-[32px]')}>
            {museLogo()}
            <div
                onKeyDown={(ev) => {
                    if (ev.key === 'Enter') onSubmitHandler(ev)
                }}
                className={cn('flex flex-col gap-[24px]')}
            >
                <TextInput
                    type="text"
                    placeholder={t('Display Name') || ''}
                    {...bind.name}
                    error={bind.name.error ? bind.name.helperText : undefined}
                    helperText={undefined}
                    className={cn('md:bg-[rgba(255,255,255,0.08)]')}
                    icon={<UserIcon />}
                />

                {clientType !== 'listenonly' && (
                    <div className="hidden md:block">
                        <div className="relative w-full">
                            <CameraPreview
                                videoTrack={userState.mediaStreams.video}
                                isPermissionsDenied={isPermissionsDenied}
                            />

                            <div className="absolute top-2 right-2 z-10">
                                <Tooltip text="Virtual background">
                                    <div>
                                        <OverlayControlButton onClick={() => toggleVirtualBackgroundModal()}>
                                            <VirtualBackgroundIcon />
                                        </OverlayControlButton>
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                        <div className="flex justify-center gap-[8px] pt-[16px] h-[50px]">
                            {isPermissionsDenied && (
                                <div className="relative">
                                    <div className="absolute top-[-10px] w-[24px] h-[24px] flex justify-center items-center">
                                        <StatusErrorIcon />
                                    </div>
                                </div>
                            )}
                            <MenuButton
                                triggerClassName="rounded-[14px]"
                                onIconClick={() => {
                                    if (isPermissionsDenied) {
                                        setIsBlockedModalOpen(true)
                                    } else {
                                        toggleMyVideoDeviceMute()
                                    }
                                }}
                                icon={
                                    <div className="h-[30px] flex items-center">
                                        {isPermissionsDenied || videoMuted ? (
                                            <CameraOffIcon
                                                className="text-muse-danger"
                                                style={{ fontSize: '1.3rem' }}
                                            />
                                        ) : (
                                            <CameraIcon style={{ fontSize: '1.3rem' }} />
                                        )}
                                    </div>
                                }
                                content={
                                    isPermissionsDenied ? null : (
                                        <div className="flex flex-col gap-2">
                                            {userState.mediaSources.video.map((video) => {
                                                const isChecked = currentVideo === video.deviceId

                                                return (
                                                    <Checkbox
                                                        key={video.deviceId}
                                                        checked={isChecked}
                                                        label={video.label}
                                                        onChange={() => {
                                                            if (isChecked) {
                                                                setMediaSelection({ video: '_none' })
                                                            } else {
                                                                setMediaSelection({ video: video.deviceId })
                                                            }
                                                        }}
                                                    />
                                                )
                                            })}
                                        </div>
                                    )
                                }
                            />

                            <MenuButton
                                triggerClassName="rounded-[14px]"
                                onIconClick={() => {
                                    if (isPermissionsDenied) {
                                        setIsBlockedModalOpen(true)
                                    } else {
                                        toggleMyAudioDeviceMute()
                                    }
                                }}
                                icon={
                                    <div className="h-[30px] flex items-center">
                                        {!!atrack && (
                                            <div className="pr-1">
                                                <VolumeMeter
                                                    stream={atrack}
                                                    channelCount={1}
                                                    variant="pills"
                                                    height={24}
                                                    isMuted={audioMuted}
                                                />
                                            </div>
                                        )}
                                        {isPermissionsDenied || audioMuted ? (
                                            <MicrophoneOffIcon
                                                className="text-muse-danger"
                                                style={{ fontSize: '1.3rem' }}
                                            />
                                        ) : (
                                            <MicrophoneIcon style={{ fontSize: '1.3rem' }} />
                                        )}
                                    </div>
                                }
                                content={
                                    isPermissionsDenied ? null : (
                                        <div className="flex flex-col gap-2">
                                            {audioInputs.external.map((audioInput) => {
                                                const currMediaSelection = userState.mediaSelection
                                                const isChecked = currMediaSelection.audioInputs
                                                    .map((i) => i.deviceId)
                                                    .includes(audioInput.deviceId)
                                                return (
                                                    <Checkbox
                                                        key={audioInput.deviceId}
                                                        checked={isChecked}
                                                        label={audioInput.label}
                                                        onChange={() => {
                                                            toggleAudioDeviceSelection(audioInput)
                                                        }}
                                                    />
                                                )
                                            })}
                                        </div>
                                    )
                                }
                            />

                            {isPermissionsDenied && (
                                <div className="relative">
                                    <div className="absolute top-[-10px] w-[24px] h-[24px] flex justify-center items-center">
                                        <StatusErrorIcon />
                                    </div>
                                </div>
                            )}
                            <MenuButton
                                triggerClassName="rounded-[14px]"
                                icon={
                                    <SpeakerIcon
                                        className={cn(isPermissionsDenied && 'text-muse-danger')}
                                        style={{ fontSize: '1.3rem' }}
                                    />
                                }
                                onIconClick={isPermissionsDenied ? () => setIsBlockedModalOpen(true) : undefined}
                                content={
                                    isPermissionsDenied ? null : (
                                        <div className="flex flex-col gap-2">
                                            {speakerOptions.map((speakerOption) => {
                                                const isChecked = speakerOption.value === selectedSpeaker
                                                return (
                                                    <Checkbox
                                                        key={speakerOption.value}
                                                        checked={isChecked}
                                                        label={speakerOption.label}
                                                        onChange={() => {
                                                            handleSpeakerChange(speakerOption.value)
                                                        }}
                                                    />
                                                )
                                            })}
                                        </div>
                                    )
                                }
                            />
                        </div>
                        <div className="flex justify-center pt-[8px]">
                            <Button
                                onClick={() => setPlayingTest(!playingTest)}
                                className="t-base-light flex items-center gap-1"
                            >
                                {playingTest ? <StopIcon /> : <PlayIcon />}
                                {playingTest ? 'Stop test' : 'Test speaker'}
                            </Button>
                        </div>
                        <SpeakerTest playing={playingTest} onEnded={() => setPlayingTest(false)} />
                    </div>
                )}

                <Button onClick={onSubmitHandler} disabled={loading} className="w-full relative" variant="primary">
                    {loading ? (
                        <Loader
                            type="TailSpin"
                            color="#202122"
                            height={Constants.spinner_width_md}
                            width={Constants.spinner_width_md}
                            timeout={300000}
                        />
                    ) : isViewer ? (
                        t('Watch session')
                    ) : (
                        t('Join session')
                    )}
                    <RightArrowIcon className="absolute right-4" />
                </Button>
                <div className={cn('text-[#B1B1B1] text-center', 't-sm md:t-base')}>
                    <Trans i18nKey={'By joining, you agree to our <1>Privacy Policy</1> and <3>Terms</3>.'}>
                        By joining, you agree to our{' '}
                        <a
                            href={Constants.privacy_policy_link}
                            className={'text-[#FCE640]'}
                            onClick={(e) => {
                                e.preventDefault()
                                ElectronIPC.openLinkInBrowser(Constants.privacy_policy_link)
                            }}
                        >
                            Privacy Policy
                        </a>{' '}
                        and{' '}
                        <a
                            href={Constants.terms_and_conditions_link}
                            className="text-[#FCE640]"
                            onClick={(e) => {
                                e.preventDefault()
                                ElectronIPC.openLinkInBrowser(Constants.terms_and_conditions_link)
                            }}
                        >
                            Terms
                        </a>
                        .
                    </Trans>
                </div>
            </div>
        </div>
    )
}

const inputModel: any = {
    name: {
        errorChecks: [
            {
                trigger: ['onSubmit'],
                condition: Conditions.is_empty,
                message: () => i18n.t('A display name is required'),
            },
            {
                trigger: ['onChange', 'onSubmit'],
                condition: (value: string, formValues: { [key: string]: string }) => {
                    return value.length > Constants.max_guest_name_length
                },
                message: () => i18n.t('Exceeded maximum allowed character length'),
            },
        ],
    },
}

export default GuestJoinForm
